import "./styles.scss";
import React, { useEffect, useState } from "react";
import { useUserCached } from "../useUserCached";
import { useTheme } from "../ThemeProvider";
import { Tooltip } from "../../core/Tooltip";
import { highnote } from "@highnote/server/src/sdk";
import { AUTH_PREFIX, UserEntity } from "@highnote/server/src/core/entities";
import { ReactComponent as AuthEmailSVG } from "App/common/icons/auth-mail.svg";
import { ReactComponent as AuthAppleSVG } from "App/common/icons/auth-apple.svg";
import { ReactComponent as AuthPhoneSVG } from "App/common/icons/auth-phone.svg";
import { ReactComponent as AuthGoogleSVG } from "App/common/icons/auth-google.svg";
import { ImageWithFallback } from "../ImageWithFallback";
import { isInviteId } from "@highnote/server/src/core/shared-util";
import { useAuth } from "App/components/Auth";
import { User } from "@sentry/types";
import { Skeleton } from "../Skeleton/Skeleton";

export enum USER_AVATAR_SIZE {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
  XLARGE = "xlarge",
  XXLARGE = "xxlarge",
  XXXLARGE = "xxxlarge",
  JUMBO = "jumbo",
}

const getName = (user: Partial<UserEntity>, _alias?: string) => {
  const alias = _alias || "Anonymous";
  if (!user) return alias;
  return user?.name || alias;
};

/**
 * `UserAvatar` and `UserName` are leaves. They render only what their props
 * give them, and they do not fetch.
 *
 * They used to call `usePublicUserRaw()`, which opens one Firestore listener
 * on `public-users-v1` for each rendered instance. And each instance sat
 * next to a `useUserCached({ id })` call that had already requested the same
 * id through the batched `getUsersPublic` path. So a roster with 50 rows
 * paid for 100 document listeners on top of one batched request. A later
 * step of this migration removes the limit on roster size (today the 1 MiB
 * ceiling on the space document caps it), so the waste grows with the
 * roster. That is why this deletion lands first.
 *
 * This change is not behind a flag. It only makes the old read path cheaper,
 * and it is correct with every read flag off. Its only rollback is a revert.
 * For that reason it ships weeks before any read flag moves.
 */
const UserAvatar = ({
  user,
  userLoading,
  alias,
  size,
}: {
  user?: Partial<UserEntity>;
  userLoading?: boolean;
  alias?: string;
  size?: USER_AVATAR_SIZE;
}) => {
  const { theme } = useTheme();
  const isAnonymous = !user;
  const name = getName(user, alias);

  let miscId = 1;
  if (user?.id) {
    const n = user.id.toLowerCase()[0];
    miscId = n < "x" ? (n < "l" ? 1 : 2) : 3;
  }

  const fallback = isAnonymous
    ? name.slice(0, 2).toUpperCase()
    : name[0].toUpperCase();

  return (
    <div
      className={"highnote-user-avatar"}
      data-cypress-id="highnote-user-avatar"
      data-size={size || USER_AVATAR_SIZE.SMALL}
      data-misc-id={miscId}
      data-theme={theme}
    >
      {userLoading ? (
        <Skeleton width="100%" height="100%" shape="circle" />
      ) : (
        <ImageWithFallback
          src={highnote.getLocalFileUrl(user?.picture)}
          fallback={fallback}
          isAnonymous={isAnonymous}
        />
      )}
    </div>
  );
};

export const ConnectedUserAvatar = ({
  userId,
  alias,
  size,
}: {
  userId: Id;
  alias?: string;
  size?: USER_AVATAR_SIZE;
}) => {
  const { user, userLoading } = useUserCached({ id: userId });

  return (
    <UserAvatar
      user={user}
      userLoading={userLoading}
      alias={alias}
      size={size}
    />
  );
};

const UserName = ({
  user,
  userLoading,
  alias,
}: {
  user?: Partial<UserEntity>;
  userLoading?: boolean;
  alias?: string;
}) => {
  const { theme } = useTheme();

  if (userLoading) {
    return (
      <span className="highnote-inline-loading" data-theme={theme}>
        ...
      </span>
    );
  }

  const name = getName(user, alias);

  /* 
    OAuth account migration: This conditional verification logic will be simplified 
    once the migration that populates all oauth accounts in the database is complete.
    After migration, we can directly check user.isVerified instead of prefix matching.
  */

  /*
   Previously, we showed a "⚠️" verification badge for unverified users
   Though we've removed the badge from the UI, we want to keep the flagging logic
   in case we want to use it for a more nicely designed indicator in the future.
   
    const isInvite = isInviteId(user?.id);
    const isAnonymous = !user?.id?.includes("|");
    const userPrefix = user?.id?.split("|")[0] as AUTH_PREFIX;

    const unverified =
    !!user &&
    !user.isVerified &&
    !isInvite &&
    !isAnonymous &&
    ![AUTH_PREFIX.GOOGLE, AUTH_PREFIX.APPLE].includes(userPrefix)
   */

  return <span className="highnote-user-name">{name}</span>;
};

export const ConnectedUserName = ({
  userId,
  alias,
}: {
  userId: Id;
  alias?: string;
}) => {
  const { user, userLoading } = useUserCached({ id: userId });

  return <UserName user={user} userLoading={userLoading} alias={alias} />;
};

export const ConnectedUserIdentifier = ({
  userId,
  identifier,
  inline,
  compact,
  useNonCached,
}: {
  userId: Id;
  identifier?: string;
  inline?: boolean;
  compact?: boolean;
  useNonCached?: boolean;
}) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [copied, setCopied] = useState<boolean>();
  const { user: cachedUser, userLoading } = useUserCached({ id: userId });

  const currentUser = user?.id === cachedUser?.id ? user : cachedUser;

  const [userRAW, setUserRAW] = useState<Partial<UserEntity> | null>(null);

  useEffect(() => {
    if (userId !== user?.id && useNonCached) {
      highnote.getUserPublic({ id: userId }).then(setUserRAW);
    }
  }, [userId, user?.id]);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);

  if (userLoading) {
    return (
      <span
        className="highnote-inline-loading"
        data-theme={theme}
        data-inline={!!inline}
      >
        ...
      </span>
    );
  }

  if (!userId || !user || isInviteId(userId)) {
    return (
      <div className="highnote-user-identifier" data-inline={!!inline}>
        {!compact && (
          <span>
            {(currentUser as User)?.email ||
              identifier ||
              userId ||
              "Unknown User"}
          </span>
        )}
        <AuthEmailSVG />
      </div>
    );
  }

  const prefix = userId?.split("|")[0];
  const text = (currentUser as User).email || currentUser.name;
  const email = (currentUser as User).email || userRAW?.email;
  // displaying user ID here instead of phone number for privacy when confidential is true
  const phone = useNonCached ? userRAW?.phone : (currentUser as User).id;

  return (
    <Tooltip
      placement="top"
      // TODO: this was disabled to hide user's phone number on tooltip, and we'll likely
      // need a different approach for handling sensitive information like users phone number
      // isDisabled={!compact && !copied}
      isDisabled
      title={copied ? "Copied!" : text}
    >
      <div
        className="highnote-user-identifier"
        data-inline={!!inline}
        // onClick={() => {
        //   copy(text);
        //   setCopied(true);
        // }}
      >
        {(prefix === AUTH_PREFIX.EMAIL ||
          prefix === AUTH_PREFIX.GOOGLE ||
          prefix === AUTH_PREFIX.APPLE) && (
          <>
            {!compact ? (
              email ? (
                <span>{email}</span>
              ) : text ? (
                <span>{text}</span>
              ) : null
            ) : null}
            {prefix === AUTH_PREFIX.EMAIL ? (
              <AuthEmailSVG />
            ) : prefix === AUTH_PREFIX.GOOGLE ? (
              <AuthGoogleSVG />
            ) : (
              <AuthAppleSVG />
            )}
          </>
        )}
        {prefix === AUTH_PREFIX.SMS && (
          <>
            {!compact && <span>{phone}</span>}
            <AuthPhoneSVG />
          </>
        )}
      </div>
    </Tooltip>
  );
};

export const UserInfo = ({
  userId,
  alias,
  children,
}: {
  userId?: string;
  alias?: string;
  children: string | React.ReactNode;
}) => {
  return (
    <div className="highnote-user-info">
      <ConnectedUserAvatar
        userId={userId}
        alias={alias}
        size={USER_AVATAR_SIZE.XLARGE}
      />
      <div className="meta">
        <span className="name">
          <ConnectedUserName userId={userId} alias={alias} />
          <ConnectedUserIdentifier userId={userId} compact />
        </span>
        <span className="description">{children}</span>
      </div>
    </div>
  );
};
